import React, { useEffect, useState } from 'react';
import Navbar from '../common/Navbar';
import KPICard from '../common/KPICard';
import PipelineChart from '../charts/PipelineChart';
import TrendChart from '../charts/TrendChart';
import GeographyChart from '../charts/GeographyChart';
import SectorChart from '../charts/SectorChart';
import StaleLeadsWidget from './widgets/StaleLeadsWidget';
import UpcomingFollowUpsWidget from './widgets/UpcomingFollowUpsWidget';
import { 
  Users, 
  DollarSign, 
  TrendingUp, 
  Target,
  RefreshCw,
  Clock,
  AlertCircle
} from 'lucide-react';
import { dashboardService } from '../../services/api';

export default function UserDashboard() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  
  const [summary, setSummary] = useState({
    totalLeads: 0,
    pipelineValue: 0,
    actualRevenue: 0,
    activeDeals: 0,
    conversionRate: 0
  });
  
  const [pipelineData, setPipelineData] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [geographyData, setGeographyData] = useState([]);
  const [sectorData, setSectorData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setError(null);
      setLoading(true);
      
      const [
        summaryRes,
        pipelineRes,
        trendRes,
        geographyRes,
        sectorRes,
        activityRes
      ] = await Promise.all([
        dashboardService.getSummary(),
        dashboardService.getPipelineStatus(),
        dashboardService.getMonthlyTrend(),
        dashboardService.getGeography(),
        dashboardService.getSectorPerformance(),
        dashboardService.getRecentActivity()
      ]);

      const getSafeData = (response) => {
        if (response?.data?.data) return response.data.data;
        if (response?.data) return response.data;
        return null;
      };

      const summaryData = getSafeData(summaryRes);
      if (summaryData) {
        setSummary({
          totalLeads: summaryData.totalLeads || 0,
          pipelineValue: summaryData.pipelineValue || 0,
          actualRevenue: summaryData.actualRevenue || 0,
          activeDeals: summaryData.activeDeals || 0,
          conversionRate: summaryData.conversionRate || 0
        });
      }
      
      const pipelineArray = getSafeData(pipelineRes);
      if (Array.isArray(pipelineArray)) {
        const pipelineWithColors = pipelineArray.map(item => ({
          ...item,
          color: item.color || getStatusColor(item.status || item.name)
        }));
        setPipelineData(pipelineWithColors);
      }
      
      const trendArray = getSafeData(trendRes);
      if (Array.isArray(trendArray)) {
        setTrendData(trendArray);
      }
      
      const geographyArray = getSafeData(geographyRes);
      if (Array.isArray(geographyArray)) {
        setGeographyData(geographyArray);
      }
      
      const sectorArray = getSafeData(sectorRes);
      if (Array.isArray(sectorArray)) {
        setSectorData(sectorArray.slice(0, 5));
      }
      
      const activityArray = getSafeData(activityRes);
      if (Array.isArray(activityArray)) {
        setRecentActivity(activityArray);
      }
      
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadDashboardData();
  };

  const getStatusColor = (status) => {
    const colors = {
      'Blue sky': '#3b82f6',
      'Initial Contact': '#3b82f6',
      'Qualified': '#10b981',
      'Proposal Phase': '#f59e0b',
      'Proposal Sent': '#f59e0b',
      'Negotiation': '#8b5cf6',
      'Project execution': '#10b981',
      'Closed Won': '#10b981',
      'Closed Lost': '#ef4444',
      'On Hold': '#6b7280',
      'Cancelled': '#6b7280'
    };
    return colors[status] || '#94a3b8';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading && !refreshing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-400 text-lg">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !refreshing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <Navbar />
        <div className="p-8">
          <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Error Loading Dashboard</h2>
            <p className="text-red-400 mb-6">{error}</p>
            <button
              onClick={loadDashboardData}
              className="px-6 py-3 bg-red-600 hover:bg-red-500 text-white rounded-lg font-semibold transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <Navbar />
      
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Sales Dashboard</h1>
            <p className="text-slate-400">Real-time sales pipeline overview and analytics</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-lg font-semibold flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <KPICard
            icon={Users}
            title="Total Leads"
            value={summary.totalLeads}
            subtitle={`${summary.activeDeals} active deals`}
            color="blue"
            trend="up"
            trendValue="+12%"
          />
          <KPICard
            icon={DollarSign}
            title="Pipeline Value"
            value={formatCurrency(summary.pipelineValue)}
            subtitle={`${formatCurrency(summary.actualRevenue)} actual`}
            color="emerald"
            trend="up"
            trendValue="+8%"
          />
          <KPICard
            icon={Target}
            title="Conversion Rate"
            value={`${summary.conversionRate}%`}
            subtitle="Closed won rate"
            color="purple"
            trend="neutral"
          />
          <KPICard
            icon={TrendingUp}
            title="Active Deals"
            value={summary.activeDeals}
            subtitle="In progress"
            color="amber"
            trend="up"
            trendValue="+5"
          />
        </div>

        {/* Widgets Row - NEW! */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <StaleLeadsWidget />
          <UpcomingFollowUpsWidget />
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
            <h2 className="text-xl font-bold text-white mb-4">Pipeline Status</h2>
            {pipelineData.length > 0 ? (
              <div className="h-80">
                <PipelineChart data={pipelineData} />
              </div>
            ) : (
              <div className="h-80 flex items-center justify-center text-slate-500">
                No pipeline data available
              </div>
            )}
          </div>

          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
            <h2 className="text-xl font-bold text-white mb-4">6-Month Trend</h2>
            {trendData.length > 0 ? (
              <div className="h-80">
                <TrendChart data={trendData} />
              </div>
            ) : (
              <div className="h-80 flex items-center justify-center text-slate-500">
                No trend data available
              </div>
            )}
          </div>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
            <h2 className="text-xl font-bold text-white mb-4">Top Sectors</h2>
            {sectorData.length > 0 ? (
              <div className="h-80">
                <SectorChart data={sectorData} />
              </div>
            ) : (
              <div className="h-80 flex items-center justify-center text-slate-500">
                No sector data available
              </div>
            )}
          </div>

          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
            <h2 className="text-xl font-bold text-white mb-4">Geography Distribution</h2>
            {geographyData.length > 0 ? (
              <div className="h-80">
                <GeographyChart data={geographyData} />
              </div>
            ) : (
              <div className="h-80 flex items-center justify-center text-slate-500">
                No geography data available
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Recent Activity</h2>
              <p className="text-slate-500 text-sm">Latest updates</p>
            </div>
          </div>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {recentActivity.length > 0 ? (
              recentActivity.map((activity, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700"
                >
                  <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm">{activity.action}</p>
                    <p className="text-slate-400 text-xs mt-1">{activity.details}</p>
                    <p className="text-slate-500 text-xs mt-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(activity.created_at)}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-slate-500">
                No recent activity
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}